/**
 * @fileoverview Shared searchable field palette for the report canvas — category
 *             tree, search filter, live collection counts, add-on-pick.
 * @module ui-admin/components/ReportCanvasBuilder/ReportFieldPalette
 *
 * Responsibilities: - Load the host's field catalog + collection counts via an
 *                     injected loader (the host's reportDataService owns the
 *                     Dexie catalog — this component stays service-agnostic).
 *                   - Render the category/field tree with search filtering,
 *                     per-category row counts, and a refresh control.
 * Dependencies:     - React, lucide-react, ./reportDefaults (catalog entry type).
 * Architecture:     - Presentation-only; the field catalog arrives via the
 *                     loadCatalog injection so the component can live in the
 *                     shared package while tenant-web and system-web keep their
 *                     own local-first data services.
 * Notes:            - Replaces the per-host DataPalette copies (was tenant-web's
 *                     ReportBuilder/DataPalette + system-web's inline palette).
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, ChevronRight, ChevronDown, Plus, Hash, Calendar, Type, ToggleLeft, Database, RefreshCw,
} from 'lucide-react';
import type { ReportFieldCatalogEntry } from './reportDefaults';

/**
 * @contract ReportFieldPaletteProps
 * Purpose: Props for the shared field palette.
 * Fields:
 * - loadCatalog: injected loader returning the field catalog + collection
 *   row-counts (host resolves via its local-first service).
 * - onPick: called with (field, label, type) when the user picks a field —
 *   the canvas's renderPalette context supplies this.
 * - selectedFields: fields already on the canvas (shown as picked/disabled).
 * - defaultExpanded: category names expanded on first render.
 */
export interface ReportFieldPaletteProps {
  loadCatalog: () => Promise<{
    fields: ReportFieldCatalogEntry[];
    counts: Record<string, number>;
  }>;
  onPick: (field: string, label: string, type: string) => void;
  selectedFields: string[];
  defaultExpanded?: string[];
}

const TYPE_ICONS: Record<string, typeof Hash> = {
  string: Type,
  number: Hash,
  date: Calendar,
  boolean: ToggleLeft,
};

/**
 * @component ReportFieldPalette
 * Purpose:     Searchable category tree of report fields for the canvas palette.
 * Props:       ReportFieldPaletteProps.
 * Behavior:    Loads once on mount; refresh button reloads counts; picking an
 *              already-selected field is a no-op.
 * Data Source: injected (host local-first service).
 * Constraints: presentation-only; no persistence.
 */
export function ReportFieldPalette({
  loadCatalog, onPick, selectedFields, defaultExpanded,
}: ReportFieldPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(defaultExpanded ?? []));
  const [fieldCatalog, setFieldCatalog] = useState<ReportFieldCatalogEntry[]>([]);
  const [collectionCounts, setCollectionCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const { fields, counts } = await loadCatalog();
        if (!cancelled) {
          setFieldCatalog(fields);
          setCollectionCounts(counts);
          if (!defaultExpanded) {
            setExpandedCategories(new Set(fields.slice(0, 2).map((f) => f.category)));
          }
        }
      } catch (err) {
        console.error('[ReportFieldPalette] Failed to load field catalog:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [loadCatalog, defaultExpanded]);

  const refreshCounts = async () => {
    try {
      const { counts } = await loadCatalog();
      setCollectionCounts(counts);
    } catch (err) {
      console.error('[ReportFieldPalette] Failed to refresh counts:', err);
    }
  };

  const categories = useMemo(() => {
    const cats = new Map<string, ReportFieldCatalogEntry[]>();
    fieldCatalog.forEach((field) => {
      if (!cats.has(field.category)) cats.set(field.category, []);
      cats.get(field.category)!.push(field);
    });
    return cats;
  }, [fieldCatalog]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    const filtered = new Map<string, ReportFieldCatalogEntry[]>();
    categories.forEach((fields, category) => {
      const matching = fields.filter(
        (f) => f.label.toLowerCase().includes(query)
          || f.field.toLowerCase().includes(query)
          || category.toLowerCase().includes(query),
      );
      if (matching.length > 0) filtered.set(category, matching);
    });
    return filtered;
  }, [categories, searchQuery]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const getCategoryCount = (category: string): number | undefined => {
    const fields = categories.get(category);
    if (!fields || fields.length === 0) return undefined;
    const prefix = fields[0].field.split('.')[0];
    return collectionCounts[prefix];
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
        <span className="mt-2 text-xs text-gray-500">Loading fields...</span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="report-field-palette">
      <div className="flex items-center justify-between px-3 pb-1 pt-3">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Data Fields</span>
        <button type="button" onClick={() => void refreshCounts()} aria-label="Refresh record counts"
          className="rounded-sm p-1 transition-colors hover:bg-gray-100">
          <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>

      <div className="px-3 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search fields..."
            aria-label="Search fields"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-hidden focus:ring-2 focus:ring-orange-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {Array.from(filteredCategories.entries()).map(([category, fields]) => (
          <div key={category} className="mb-1">
            <button type="button" onClick={() => toggleCategory(category)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100">
              {expandedCategories.has(category)
                ? <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                : <ChevronRight className="h-3.5 w-3.5 text-gray-500" />}
              <Database className="h-3.5 w-3.5 text-gray-500" />
              <span className="flex-1 text-left">{category}</span>
              <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-400" title={`${getCategoryCount(category) ?? 0} records`}>
                {fields.length} fields{getCategoryCount(category) !== undefined ? ` · ${getCategoryCount(category)} rows` : ''}
              </span>
            </button>

            {expandedCategories.has(category) && (
              <div className="ml-4 mt-0.5 space-y-0.5">
                {fields.map((field) => {
                  const isSelected = selectedFields.includes(field.field);
                  const TypeIcon = TYPE_ICONS[field.type] || Type;
                  return (
                    <button
                      key={field.field}
                      type="button"
                      onClick={() => { if (!isSelected) onPick(field.field, field.label, field.type); }}
                      disabled={isSelected}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors ${
                        isSelected ? 'cursor-default bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <TypeIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1 truncate text-left">{field.label}</span>
                      {!isSelected && <Plus className="h-3.5 w-3.5 text-gray-400" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {filteredCategories.size === 0 && (
          <div className="py-8 text-center text-sm text-gray-500">No fields match &quot;{searchQuery}&quot;</div>
        )}
      </div>
    </div>
  );
}
